import { Grid, TableCell, Link, Typography } from "@mui/material";
import { FrameWithSearchBox } from "../common/Frame";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { addDays, format, formatISO, isValid, parse, startOfDay, subMonths } from "date-fns";
import qs from "qs";
import { DATE_FORMAT, getDateFnsLocale } from "../date-utils";
import useAxios from "axios-hooks";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { DateRange } from "../common/DateRange";
import { TableTemplate as TableTemplate } from "../common/Table";
import { MaterialSummary } from "../schemas";
import ErrorHandler from "../ErrorHandler";

const locale = getDateFnsLocale();

export function Summary() {

  const [searchParam, setSearchParams] = useSearchParams();

  const paramFrom = parse(searchParam.get('from') || '', DATE_FORMAT, new Date());
  const paramTo = parse(searchParam.get('to') || '', DATE_FORMAT, new Date());

  const today = startOfDay(new Date());
  const initFrom = isValid(paramFrom) ? paramFrom : subMonths(addDays(today, 1), 1);
  const initTo = isValid(paramTo) ? paramTo : today;

  const [from, setFrom] = useState<Date | null>(initFrom);
  const [to, setTo] = useState<Date | null>(initTo);

  const [page, setPage] = useState(Number(searchParam.get('page') || 0));
  const [rowsPerPage, setRowsPerPage] = useState(Number(searchParam.get('rowsperpage') || 10));

  const { user } = useAuthenticator();
  const token = user?.getSignInUserSession()?.getIdToken().getJwtToken();

  const url = `${process.env.REACT_APP_AWS_API_GATEWAY_URL}/material/summary`;

  const [{ data, loading, error }, refetch] = useAxios<{ count: number, items: MaterialSummary[] }>({
    url,
    headers: { Authorization: token },
    params: { from: formatISO(initFrom), to: formatISO(addDays(initTo, 1)), limit: rowsPerPage, offset: page * rowsPerPage }
  });

  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const currentParams = Object.fromEntries(searchParam.entries());

  const onSearchClicked = () => {
    if (!isValid(from) || !isValid(to)) return;

    const updatedParams = {
      ...currentParams,
      from: from ? format(from, DATE_FORMAT) : currentParams.from,
      to: to ? format(to, DATE_FORMAT) : currentParams.to,
      rowsperpage: '10',
      page: '0',
    };
  
    setSearchParams(qs.stringify(updatedParams), { replace: true });
    setPage(0);
    setRowsPerPage(10);
  }

  const onResetClicked = () => {
    setSearchParams("", { replace: true });
    setFrom(subMonths(addDays(today, 1), 1));
    setTo(today);
    setPage(0);
    setRowsPerPage(10);
  }

  const errorMessage = (error && (error.response?.data?.message || error.message)) || '';
  const errorCode = (error && (error.response?.data?.code || error.code)) || '';

  useEffect(() => {
    if (error) {
      setOpen(true);
    } 
  }, [error]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <FrameWithSearchBox
        title="Summary"
        onSearchClicked={onSearchClicked}
        onResetClicked={onResetClicked}
      >
        <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
          <DateRange from={from} setFrom={setFrom} to={to} setTo={setTo} required />
        </Grid>
        <TableTemplate
          columns={[
            { name: 'Material Name', props: { width: "60%" } },
            { name: 'Date', props: { width: "20%" } },
            { name: 'Count', props: { width: "20%", align: "right" } },
          ]}
          dataRows={data?.items}
          count={data?.count}
          rowRenderFunc={row => [
            <TableCell key={0} colSpan={2} sx={{ p: 0 }}>
              <Link
                href={`overview/${row.material_id}?from=${row.date}&to=${row.date}`}
                sx={{ width: '100%', display: 'flex' }}
              >
                <Typography variant="inherit" sx={{ width: "75%", pl: 2 }}>
                  {row.material_name}
                </Typography>
                <Typography variant="inherit" sx={{ width: "25%", pl: 2 }}>
                  {format(parse(row.date, DATE_FORMAT, new Date()), 'PP', { locale })}
                </Typography>
              </Link>
            </TableCell>,
            <TableCell key={2} align="right">{row.count}</TableCell>,
          ]}
          loading={loading}
          error={error}
          page={page}
          onPageChange={page => {
            setPage(page);
            setSearchParams(
              { ...currentParams, page: page.toString() }, 
              { replace: true }
            );
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={v => {
            setPage(0);
            setRowsPerPage(v);
            setSearchParams(
              { ...currentParams, rowsperpage: v.toString() }, 
              { replace: true }
            );
          }}
        />
      </FrameWithSearchBox>
      <ErrorHandler open={open} onClose={onClose} errorMessage={errorMessage} errorCode={errorCode} />
    </LocalizationProvider>
  )
}
